<!-- components/history/QRHistory.vue - FIXED -->
<script setup>
import {
  ClockIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from "@heroicons/vue/24/outline";
import { formatDistanceToNow } from "date-fns";

// Props & Emits
const props = defineProps({
  history: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["regenerate", "delete", "clear"]);

// Methods
const formatDate = (date) => {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
};

const formatFileSize = (bytes) => {
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const getDisplayContent = (item) => {
  switch (item.type) {
    case "vcard":
      if (typeof item.content === "object") {
        return (
          item.content.name ||
          `${item.content.firstName} ${item.content.lastName}`.trim() ||
          "Contact"
        );
      }
      return "Contact Card";
    case "wifi":
      if (typeof item.content === "object") {
        return `WiFi: ${item.content.ssid}`;
      }
      return "WiFi Network";
    case "sms":
      return `SMS: ${item.content.split(":")[1] || "Phone Number"}`;
    case "email":
      return `Email: ${
        item.content.split("mailto:")[1]?.split("?")[0] || "Email Address"
      }`;
    default:
      return typeof item.content === "string"
        ? item.content.length > 50
          ? item.content.substring(0, 50) + "..."
          : item.content
        : "QR Code";
  }
};

const downloadItem = (item) => {
  const link = document.createElement("a");
  link.download = `qrcode-${item.id}-${Date.now()}.png`;
  link.href = item.qrCode;
  link.click();
};
</script>

<template>
  <div class="space-y-4 min-w-0">
    <div class="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-2">
      <h3 class="text-lg font-semibold text-gray-800 flex items-center">
        <ClockIcon class="w-5 h-5 mr-2" />
        Recent QR Codes
      </h3>
      <div class="flex space-x-2">
        <button
          v-if="history.length > 0"
          type="button"
          @click="emit('clear')"
          class="min-h-[44px] px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
        >
          Clear All
        </button>
      </div>
    </div>

    <!-- History List -->
    <div
      v-if="history.length > 0"
      class="space-y-3 max-h-96 overflow-y-auto custom-scrollbar"
    >
      <div
        v-for="item in history.slice(0, 10)"
        :key="item.id"
        class="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
      >
        <div class="flex flex-col tablet:flex-row tablet:items-center gap-3 tablet:gap-4">
          <!-- QR Code Thumbnail + Content (stack on mobile) -->
          <div class="flex items-center space-x-4 min-w-0 flex-1">
            <img
              :src="item.qrCode"
              :alt="`QR Code for ${item.type}`"
              class="w-16 h-16 object-cover rounded border shrink-0"
            />
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2 mb-1">
              <span
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize"
              >
                {{ item.type }}
              </span>
              <span class="text-xs text-gray-500">
                {{ formatDate(item.timestamp) }}
              </span>
            </div>

            <p class="text-sm text-gray-800 truncate">
              {{ getDisplayContent(item) }}
            </p>

            <div class="flex items-center space-x-4 mt-2 text-xs text-gray-500">
              <span>{{ item.options.size }}px</span>
              <span>{{ item.options.errorCorrectionLevel }}</span>
              <span v-if="item.metadata?.fileSize">
                {{ formatFileSize(item.metadata.fileSize) }}
              </span>
            </div>
          </div>
          </div>

          <!-- Actions (touch-friendly 44px) -->
          <div class="flex items-center justify-start tablet:justify-end space-x-2 shrink-0">
            <button
              type="button"
              @click="emit('regenerate', item)"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
              title="Regenerate"
            >
              <ArrowPathIcon class="w-4 h-4" />
            </button>
            <button
              type="button"
              @click="downloadItem(item)"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
              title="Download"
            >
              <ArrowDownTrayIcon class="w-4 h-4" />
            </button>
            <button
              type="button"
              @click="emit('delete', item.id)"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <TrashIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <ClockIcon class="w-12 h-12 mx-auto text-gray-400 mb-3" />
      <p class="text-gray-500">No QR codes generated yet</p>
      <p class="text-sm text-gray-400">Your recent QR codes will appear here</p>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>